/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { Helmet, HelmetProvider } from 'react-helmet-async'
import { IntlProvider } from 'react-intl'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import AuthProvider from '../../../modules/auth/components/AuthProvider'
import { getAppName, getAppVersion } from '../lib/app'
import { queryClient } from '../lib/query'
import { QueryClientProvider } from '@tanstack/react-query'
import { useMemo } from 'react'
import { DateTime } from 'luxon'
import useAuth, { AuthInfo, getPermissions, isSignedIn } from '../../../modules/auth/hooks/useAuth'
import {
  fetchAuthDetails,
  signInWithPassword,
  signOut
} from '../../../modules/auth/clients/authAPIClient'
import { getSignInLink } from '../../../modules/auth/lib/authLinks'
import { MantineProvider } from '@mantine/core'
import { Notifications } from '@mantine/notifications'

import '@mantine/core/styles.css'
import '@mantine/charts/styles.css'
import '@mantine/notifications/styles.css'
import { theme } from '../lib/theme'
import SettingsProvider from '../../../modules/twh/components/SettingsProvider'
import { routes } from '../routes'
import { getMessages } from '../../../common/lib/messages'

function App () {
  const auth = useAuth<AuthInfo>({
    getPermissions,
    isSignedIn,
    refresh: fetchAuthDetails,
    signInFunc: signInWithPassword,
    signInUrl: getSignInLink(),
    signOutFunc: signOut
  })

  const locale = useMemo(() => navigator.language, [])
  const timezone = useMemo(() => DateTime.local().zoneName || 'Pacific/Tahiti', [])

  const router = createBrowserRouter(routes)

  return (
    <HelmetProvider>
      <Helmet>
        <title>{`${getAppName()} v${getAppVersion()}`}</title>
        <meta
          name="robots"
          content="noindex,nofollow"
        />
      </Helmet>
      <QueryClientProvider client={queryClient}>
        <SettingsProvider>
          <MantineProvider theme={theme}>
            <IntlProvider
              defaultLocale="fr"
              messages={getMessages(locale)}
              locale={locale}
              onError={() => {
                // todo handle error
              }}
              timeZone={timezone}
            >
              <AuthProvider context={auth}>
                <Notifications />
                <RouterProvider router={router} />
              </AuthProvider>
            </IntlProvider>
          </MantineProvider>
        </SettingsProvider>
      </QueryClientProvider>
    </HelmetProvider>
  )
}

export default App
